import React from 'react';
import { useParams } from 'react-router-dom';
import ClusterDisplay from './ClusterDisplay';
import LoadingDisplay from './LoadingDisplay';
import ErrorDisplay from './ErrorDisplay';
import UnansweredQuestions from './UnansweredQuestions';
import ShowJsonResultButton from './ShowJsonResultButton';
import Card from './Card';
import { useResults } from '../hooks/useResults';

import { CalculatedResults } from '../utils/calculations';
import { TestResult } from '../services/resultService';
import questionsData from '../data/questions.json';
import { Question } from '../utils/questionService';

interface Profile {
  name?: string;
  description?: string;
  [key: string]: unknown;
}

interface ResultsPageProps {}

const ResultsPage: React.FC<ResultsPageProps> = () => {
  const { resultId } = useParams<{ resultId: string }>();
  const {
    results,
    unansweredQuestions,
    isSaving,
    error,
    status,
    completionMessage,
    handleUpdate,
  } = useResults(resultId);

  // Handle loading state
  if (status === 'loading') {
    return <LoadingDisplay text="Загрузка результатов..." />;
  }

  // Handle error state
  if (error || status === 'error') {
    return <ErrorDisplay error={error instanceof Error ? error.message : 'Произошла ошибка при загрузке результатов'} />;
  }

  // Handle case when results are not available
  if (!results || (Object.keys(results).length === 0 && results.constructor === Object)) {
    return <ErrorDisplay error="Результаты не найдены" />;
  }

  // Типизация результатов
  const typedResults = results as TestResult & CalculatedResults;
  
  // Проверяем, есть ли данные для отображения
  const hasResults = typedResults && (
    (typedResults.scores && Object.keys(typedResults.scores).length > 0) ||
    (typedResults.profiles && typedResults.profiles.length > 0) ||
    typedResults.cluster
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-indigo-50 py-10 animate-fade-in">
      <div className="max-w-4xl mx-auto px-4 sm:px-8">
        <h1 className="text-4xl font-extrabold text-center text-indigo-900 mb-10 flex items-center justify-center gap-3 animate-slide-down">
          <span role="img" aria-label="result">🎯</span> Результаты теста
        </h1>
        {/* Unanswered questions section moved to top */}
        <div className="mb-8 animate-fade-in">
          <UnansweredQuestions
            unansweredQuestions={unansweredQuestions as Question[]}
            onUpdate={handleUpdate}
            isSaving={isSaving}
            completionMessage={completionMessage}
            error={error}
          />
        </div>

        {/* Always show results section */}
        <div className="mt-8 animate-fade-in">
          <ShowJsonResultButton results={typedResults} />
          {hasResults ? (
            <>
              {/* Main content with ClusterDisplay */}
              <div className="space-y-12 animate-fade-in">
                {/* ClusterDisplay handles both Psychometric and DnD profiles */}
                <ClusterDisplay 
                  results={typedResults} 
                  questionsData={questionsData}
                />
                {/* Show message if no data is available */}
                {!typedResults.scores && !typedResults.cluster && (
                  <Card title="Нет данных" variant="warning" className="text-center">
                    <p className="text-yellow-700">Данные профиля будут доступны после ответа на дополнительные вопросы.</p>
                  </Card>
                )}
              </div>
            </>
          ) : (
            <Card title="Нет данных для отображения результатов" variant="warning" className="mb-4 text-center">
              <p className="text-yellow-700">Нет данных для отображения результатов.</p>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
};

export default ResultsPage;